<template>
  <div>
    <div class="farm-detail-header d-flex justify-content-between align-items-center mb-2 flex-wrap">
      <div class="d-flex align-items-center">
        <am-button
          class="farm-back-btn mr-1"
          variant="outline-primary"
          icon="ArrowLeftIcon"
          label="Back"
          @click="$router.push({ name: 'farms' })"
        />
        <h3 class="mb-0 ml-50">
          {{ farm ? farm.name : 'Farm details' }}
        </h3>
      </div>
      <am-button
        v-if="farm"
        icon="Edit2Icon"
        label="Edit"
        @click="setupOpened = true"
      />
    </div>

    <div
      v-if="loading"
      class="farm-detail-loading text-center py-5"
    >
      <b-spinner variant="primary" />
      <div class="text-muted mt-1">
        Loading farm details...
      </div>
    </div>

    <template v-else-if="farm">
      <b-row class="mb-2">
        <b-col md="3">
          <b-card><small>Farmer</small><div><strong>{{ farm.member ? farm.member.full_name : '-' }}</strong></div></b-card>
        </b-col>
        <b-col md="3">
          <b-card><small>Location</small><div><strong>{{ farm.location }}</strong></div></b-card>
        </b-col>
        <b-col md="2">
          <b-card><small>Area</small><div><strong>{{ farm.size }} ha</strong></div></b-card>
        </b-col>
        <b-col md="2">
          <b-card>
            <small>Mapped</small>
            <div>
              <strong>{{ farm.farm_mapped ? `Yes${farm.mapped_by ? ` · ${farm.mapped_by}` : ''}` : 'No' }}</strong>
            </div>
          </b-card>
        </b-col>
        <b-col md="2">
          <b-card><small>Crop / Land</small><div><strong>{{ farm.crop }} · {{ farm.land_ownership }}</strong></div></b-card>
        </b-col>
      </b-row>

      <b-card
        v-if="showGpsFields"
        class="mb-2"
      >
        <h5>GPS & boundary</h5>
        <p class="mb-25"><strong>GPS:</strong> {{ farm.gps || 'Not recorded' }}</p>
        <p class="mb-0 text-muted"><strong>Polygon:</strong> {{ farm.polygon || 'Not recorded' }}</p>
      </b-card>

      <b-card>
        <div class="d-flex justify-content-between align-items-center mb-1">
          <div>
            <h5 class="mb-0">
              Yields
            </h5>
            <small class="text-muted">
              {{ summary.yields_count || 0 }} records ·
              Expected {{ summary.total_expected || 0 }} ·
              Actual {{ summary.total_actual || 0 }}
            </small>
          </div>
          <am-button
            icon="PlusIcon"
            label="Add yield"
            @click="openYield()"
          />
        </div>

        <b-table
          :items="farm.yields || []"
          :fields="yieldFields"
          small
          responsive
        >
          <template #cell(recorded_on)="data">
            {{ formatDate(data.item.recorded_on) }}
          </template>
          <template #cell(actions)="data">
            <am-button
              icon-only
              icon="Edit2Icon"
              variant="flat-primary"
              @click="openYield(data.item)"
            />
            <am-button
              icon-only
              icon="TrashIcon"
              variant="flat-danger"
              @click="removeYield(data.item)"
            />
          </template>
        </b-table>
      </b-card>
    </template>

    <farm-setup
      :modal-opened="setupOpened"
      :farm="farm"
      @modalClosed="setupOpened = false"
      @saved="onFarmSaved"
    />

    <am-modal
      :show="yieldOpened"
      :title="editingYield && editingYield.id ? 'Edit yield' : 'Add yield'"
      size="lg"
      :loading="savingYield"
      ok-text="Save yield"
      @onClose="yieldOpened = false"
      @onOk="saveYield"
    >
      <div class="row">
        <div class="col-md-6">
          <am-input
            v-model="yieldForm.season"
            name="season"
            label="Season"
            rules="required"
          />
        </div>
        <div class="col-md-6">
          <am-input
            v-model="yieldForm.crop"
            name="crop"
            label="Crop"
            rules="required"
          />
        </div>
        <div class="col-md-6">
          <am-input
            v-model="yieldForm.expected"
            name="expected"
            label="Expected output"
            type="number"
            rules="required|numeric|min_value:0"
          />
        </div>
        <div class="col-md-6">
          <am-input
            v-model="yieldForm.actual"
            name="actual"
            label="Actual output"
            type="number"
            rules="required|numeric|min_value:0"
          />
        </div>
        <div class="col-md-6">
          <am-date-picker
            v-model="yieldForm.recorded_on"
            name="recorded_on"
            label="Recorded on"
          />
        </div>
      </div>
      <template slot="modal-actions">
        <b-row class="float-right">
          <am-button
            name="cancel"
            variant="flat-danger"
            icon="XIcon"
            class="mr-1"
            label="Cancel"
            @click="yieldOpened = false"
          />
          <am-button
            name="save_yield"
            label="Save yield"
            :loading="savingYield"
            loading-text="Loading..."
            @click="saveYield"
          />
        </b-row>
      </template>
    </am-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BSpinner, BTable,
} from 'bootstrap-vue'
import AmButton from '@/farmbusiness/components/AmButton.vue'
import AmModal from '@/farmbusiness/components/AmModal.vue'
import AmInput from '@/farmbusiness/components/AmInput.vue'
import AmDatePicker from '@/farmbusiness/components/AmDatePicker.vue'
import FarmSetup from '@/views/farm-manager/farms/FarmSetup.vue'
import showToast from '@/farmbusiness/mixins/showToast'
import confirm from '@/farmbusiness/mixins/confirm'

export default {
  name: 'FarmDetail',
  components: {
    AmButton, AmModal, AmInput, AmDatePicker, FarmSetup, BRow, BCol, BCard, BSpinner, BTable,
  },
  mixins: [showToast, confirm],
  data() {
    return {
      loading: false,
      setupOpened: false,
      yieldOpened: false,
      savingYield: false,
      editingYield: null,
      // Temporarily hidden; set true to re-enable GPS/polygon display
      showGpsFields: false,
      yieldForm: {
        season: null,
        crop: null,
        expected: null,
        actual: null,
        recorded_on: null,
      },
      yieldFields: [
        { key: 'season', label: 'Season' },
        { key: 'crop', label: 'Crop' },
        { key: 'expected', label: 'Expected' },
        { key: 'actual', label: 'Actual' },
        { key: 'recorded_on', label: 'Date' },
        { key: 'actions', label: '' },
      ],
    }
  },
  computed: {
    farm() {
      return this.$store.getters['farmManager/currentFarm']
    },
    summary() {
      return this.$store.getters['farmManager/farmSummary'] || {}
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      this.$store.dispatch('farmManager/fetchFarm', this.$route.params.id)
        .finally(() => { this.loading = false })
    },
    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    },
    toDateInputValue(value) {
      if (!value) return null
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    onFarmSaved() {
      this.setupOpened = false
      this.load()
    },
    openYield(row = null) {
      this.editingYield = row
      this.yieldForm = {
        season: row?.season || null,
        crop: row?.crop || this.farm?.crop || null,
        expected: row?.expected ?? null,
        actual: row?.actual ?? null,
        recorded_on: this.toDateInputValue(row?.recorded_on) || null,
      }
      this.yieldOpened = true
    },
    saveYield() {
      this.savingYield = true
      const payload = {
        ...this.yieldForm,
        recorded_on: this.toDateInputValue(this.yieldForm.recorded_on),
      }
      const action = this.editingYield?.id
        ? this.$store.dispatch('farmManager/updateYield', { yieldId: this.editingYield.id, data: payload })
        : this.$store.dispatch('farmManager/createYield', { farmId: this.farm.id, data: payload })
      action.then(() => {
        this.showSuccess('Yield saved')
        this.yieldOpened = false
        this.load()
      }).catch(() => this.showError('Failed to save yield'))
        .finally(() => { this.savingYield = false })
    },
    removeYield(row) {
      this.confirmAction('Delete this yield?').then(ok => {
        if (!ok) return
        this.$store.dispatch('farmManager/deleteYield', row.id).then(() => {
          this.showSuccess('Yield deleted')
          this.load()
        }).catch(() => this.showError('Failed to delete yield'))
      })
    },
  },
}
</script>

<style scoped>
.farm-detail-loading {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.farm-back-btn {
  white-space: nowrap;
}

.farm-back-btn >>> .btn,
.farm-back-btn >>> div {
  white-space: nowrap;
}
</style>
